import { useState, useRef, useEffect, type FormEvent } from "react"
import { createPortal } from "react-dom"
import ReactMarkdown from "react-markdown"
import { Sparkles, Send, MessageCircle, X, MessageSquarePlus, Headphones, TrendingUp, Calendar, Info, Package, Loader2 } from "lucide-react"
import { useLocation } from "react-router-dom"
import { useModalOpen } from "../lib/useModalOpen"
import { useAuth } from "./AuthProvider"
import { useCortexModal } from "./CortexModalContext"
import { useCortexContext } from "./CortexContext"
import { CortexAvatar } from "./CortexAvatar"
import { GlitchWord } from "./GlitchWord"
import { useGlassTokens } from "../lib/useAppBackground"

const SUGGESTED_QUESTIONS = [
  "What's my Luster Score trend?",
  "When is my next clean?",
  "Summarize recent issues",
  "How do I add a new report?",
]

const ASSISTANT_ERROR_FALLBACK = "Hmm, I lost my train of thought for a moment. Mind asking again? ✨"

const PLACEHOLDER_PHRASES = ["One sec…", "Looking that up…", "Checking your properties…", "On it…"]
function getPlaceholderPhrase(): string {
  return PLACEHOLDER_PHRASES[Math.floor(Math.random() * PLACEHOLDER_PHRASES.length)]
}

function getSupabaseFunctionsConfig(): { baseUrl: string; anonKey: string } {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || url === "YOUR_SUPABASE_URL" || !anonKey || anonKey === "YOUR_SUPABASE_ANON_KEY")
    return { baseUrl: "", anonKey: "" }
  return { baseUrl: url.replace(/\/$/, "") + "/functions/v1", anonKey }
}

export type ChatMessage = { role: "user" | "assistant"; text: string; isPlaceholder?: boolean }

export type CortexAssistantPayload = {
  messages: ChatMessage[]
  userContext?: { role: string | null; clientId: string | null; companyId: string | null; propertyIds: string[]; currentPage?: string }
  /** Sent in body so the function can auth when the gateway does not forward the Authorization header. */
  access_token?: string
}

/** Call the cortex edge function. Uses session access_token for auth (header + body fallback). */
export async function callCortexAssistant(
  payload: CortexAssistantPayload,
  accessToken: string | undefined,
  baseUrl: string
): Promise<{ reply: string } | { error: string }> {
  if (!baseUrl) return { error: "Assistant is not configured." }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  const body: Record<string, unknown> = { ...payload }

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`
    body.access_token = accessToken
  }

  const res = await fetch(`${baseUrl}/cortex`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  })
  let data: { reply?: string; error?: string }
  try {
    data = (await res.json()) as { reply?: string; error?: string }
  } catch {
    return { error: res.status === 401 ? "Session expired. Please sign in again." : ASSISTANT_ERROR_FALLBACK }
  }
  if (!res.ok) return { error: data.error ?? ASSISTANT_ERROR_FALLBACK }
  if (typeof data.reply === "string") return { reply: data.reply }
  return { error: data.error ?? ASSISTANT_ERROR_FALLBACK }
}

/**
 * Cortex Assistant — In-card conversation; cycling placeholder until focus; Expand for full modal.
 */
export function CortexAssistant() {
  const glass = useGlassTokens()
  const [query, setQuery] = useState("")
  const [expandOpen, setExpandOpen] = useState(false)
  const [cyclingPlaceholder, setCyclingPlaceholder] = useState(0)
  const cortexModal = useCortexModal()
  useModalOpen(expandOpen)

  useEffect(() => {
    const t = setInterval(() => {
      setCyclingPlaceholder((i: number) => (i + 1) % SUGGESTED_QUESTIONS.length)
    }, 2500)
    return () => clearInterval(t)
  }, [])

  /** This card is a pure launcher — all interaction opens the full modal immediately. */
  const openModal = (initialQuery?: string) => {
    const initialMessages: ChatMessage[] = initialQuery?.trim()
      ? [{ role: "user", text: initialQuery.trim() }]
      : []
    if (cortexModal?.openCortex) {
      cortexModal.openCortex(initialMessages)
    } else {
      setExpandOpen(true)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const text = query.trim()
    if (!text) return
    setQuery("")
    openModal(text)
  }

  return (
    <>
      <section
        className="rounded-2xl flex flex-col h-full min-h-0 min-w-0 p-[2px] shadow-sm"
        style={{
          background: "linear-gradient(135deg, rgb(67 81 182) 0%, rgb(102 117 200) 50%, rgb(147 51 234) 100%)",
        }}
      >
        <div className="rounded-[calc(1rem-2px)] bg-white dark:bg-stone-900 flex flex-col h-full min-h-0 overflow-hidden border border-stone-200/50 dark:border-stone-800/50 relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 dark:bg-indigo-400/5 blur-3xl pointer-events-none" />
          <div className="p-5 md:p-6 flex flex-col h-full min-h-0 relative z-10">

            {/* Header */}
            <div className="flex items-center gap-4 mb-4 shrink-0">
              <div
                className="rounded-full bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 dark:from-indigo-900/40 dark:to-purple-900/40 overflow-hidden flex items-center justify-center shrink-0 border border-indigo-200/50 dark:border-indigo-700/50 w-14 h-14 shadow-inner"
                data-shader-slot-assistant-icon
              >
                <Sparkles className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-pulse" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-stone-900 dark:text-stone-100 truncate flex items-center gap-2">
                  Cortex
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-bold uppercase tracking-wider">Beta</span>
                </h2>
                <p className="text-xs font-medium text-stone-500 dark:text-stone-400">Your intelligent facility assistant</p>
              </div>
            </div>

            {/* Clickable center area — opens modal */}
            <div
              className="flex-1 min-h-0 flex flex-col items-center justify-center text-center p-4 space-y-4 cursor-pointer rounded-2xl hover:bg-stone-50/80 dark:hover:bg-stone-800/30 transition-colors"
              onClick={() => openModal()}
            >
              <div className="p-3 rounded-2xl bg-white dark:bg-stone-800 shadow-sm border border-stone-100 dark:border-stone-700">
                <MessageCircle className="w-6 h-6 text-indigo-500 dark:text-indigo-400" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">Ask Cortex anything</p>
                <p className="text-xs text-stone-500 dark:text-stone-400 max-w-[220px] mx-auto italic">
                  &ldquo;{SUGGESTED_QUESTIONS[cyclingPlaceholder]}&rdquo;
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED_QUESTIONS.slice(0, 3).map((q, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openModal(q) }}
                    className="px-3 py-1.5 rounded-full text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors font-medium"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick-launch input */}
            <form onSubmit={handleSubmit} className="shrink-0 flex gap-2 mt-4">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => openModal(query || undefined)}
                placeholder={SUGGESTED_QUESTIONS[cyclingPlaceholder]}
                className="flex-1 min-w-0 px-4 py-3 rounded-2xl bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 border border-stone-200 dark:border-stone-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:focus:ring-indigo-500/30 text-sm placeholder:text-stone-400 shadow-sm transition-all cursor-pointer"
                aria-label="Ask Cortex"
                readOnly
              />
              <button
                type="button"
                onClick={() => openModal(query || undefined)}
                className="px-4 h-[46px] rounded-xl bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-md shadow-indigo-500/20"
                aria-label="Open Cortex chat"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {expandOpen && !cortexModal &&
        createPortal(
          <CortexChatModal
            onClose={() => setExpandOpen(false)}
            initialMessages={[]}
            onOpenContact={undefined}
          />,
          document.body
        )}
    </>
  )
}


export type CortexChatModalProps = {
  onClose: () => void
  initialMessages: ChatMessage[]
  onOpenContact?: () => void
}

export function CortexChatModal({
  onClose,
  initialMessages,
  onOpenContact,
}: CortexChatModalProps) {
  const glass = useGlassTokens()
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
  const [assistantLoading, setAssistantLoading] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  const { role, clientId, companyId, session } = useAuth()
  const { propertyIds } = useCortexContext()
  const { baseUrl } = getSupabaseFunctionsConfig()
  const { pathname } = useLocation()
  const currentPage = pathname.replace(/^\//, "") || "dashboard"

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // If the modal was opened with a pre-populated user message (from the inline card),
  // auto-fire the AI call immediately so the user doesn't have to click Send again.
  useEffect(() => {
    const lastMsg = initialMessages[initialMessages.length - 1]
    if (lastMsg?.role === "user" && lastMsg?.text?.trim()) {
      const fire = async () => {
        setAssistantLoading(true)
        const placeholder: ChatMessage = { role: "assistant", text: getPlaceholderPhrase(), isPlaceholder: true }
        setMessages((prev) => [...prev, placeholder])
        const result = await callCortexAssistant(
          {
            messages: initialMessages,
            userContext: { role: role ?? null, clientId: clientId ?? null, companyId: companyId ?? null, propertyIds, currentPage },
          },
          session?.access_token,
          baseUrl
        )
        setAssistantLoading(false)
        const realMessage: ChatMessage = "reply" in result
          ? { role: "assistant", text: result.reply }
          : { role: "assistant", text: result.error }
        setMessages((prev) => prev.filter((m) => !m.isPlaceholder).concat(realMessage))
      }
      fire()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const text = input.trim()
    if (!text || assistantLoading) return
    setInput("")
    const nextMessages: ChatMessage[] = [...messages, { role: "user", text }]
    const placeholder: ChatMessage = { role: "assistant", text: getPlaceholderPhrase(), isPlaceholder: true }
    setMessages([...nextMessages, placeholder])
    setAssistantLoading(true)
    const result = await callCortexAssistant(
      {
        messages: nextMessages,
        userContext: { role: role ?? null, clientId: clientId ?? null, companyId: companyId ?? null, propertyIds, currentPage },
      },
      session?.access_token,
      baseUrl
    )
    setAssistantLoading(false)
    const realMessage: ChatMessage = "reply" in result
      ? { role: "assistant", text: result.reply }
      : { role: "assistant", text: result.error }
    setMessages((prev) => prev.filter((m) => !m.isPlaceholder).concat(realMessage))
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-50 ${glass.overlay} backdrop-blur-sm`}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="cortex-chat-title"
        className="fixed inset-0 z-50 flex flex-col overflow-hidden md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:h-[85vh] md:rounded-3xl bg-stone-950/95 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] md:animate-in md:fade-in md:zoom-in-95 md:duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — glitchOS terminal glass */}
        <div className="flex items-center justify-between shrink-0 px-6 pt-7 pb-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <CortexAvatar />
            <div>
              <h2 id="cortex-chat-title" className="text-[11px] font-mono font-bold uppercase tracking-widest text-white/90 leading-tight">
                <GlitchWord word="Cortex" intensity={1} colorBase="violet" />
              </h2>
              <p className="text-[10px] font-mono text-white/40 mt-0.5">Facility intelligence · ask anything</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar — dark glass */}
          <aside className={`hidden sm:flex flex-col w-56 shrink-0 border-r border-white/[0.06] ${glass.sectionBg} p-4 gap-4`}>
            <div className="flex items-center justify-between px-2">
              <span className="text-[10px] font-mono font-bold text-white/40 uppercase tracking-widest">History</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto custom-scrollbar">
              <div className="p-3 rounded-xl bg-violet-500/10 border border-violet-400/20">
                <p className="text-[10px] font-mono font-bold text-violet-400 uppercase tracking-wider mb-0.5">Current Session</p>
                <p className="text-[10px] font-mono text-white/40">Active now</p>
              </div>
              {[1, 2].map((i) => (
                <div key={i} className="p-3 rounded-xl border border-white/[0.06] opacity-40 cursor-not-allowed">
                  <div className="h-2 w-3/4 bg-white/10 rounded mb-2" />
                  <div className="h-1.5 w-1/2 bg-white/5 rounded" />
                </div>
              ))}
            </div>
            <div className="mt-auto p-3 rounded-xl border border-white/[0.08] bg-white/[0.03]">
              <p className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1">Pro-Tip</p>
              <p className="text-[11px] font-mono text-white/40 leading-snug">Ask Cortex to summarize recent inspection notes.</p>
            </div>
          </aside>

          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex-1 overflow-y-auto p-6 md:p-8 min-h-[200px] flex flex-col custom-scrollbar">
              {messages.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md mx-auto space-y-6">
                  {/* Terminal trace */}
                  <div className={`w-full max-w-sm rounded-xl border border-white/[0.08] ${glass.cardBg} backdrop-blur-md px-4 py-3 text-left space-y-1`}>
                    <div className="font-mono text-[10px] space-y-0.5">
                      <p><span className="text-white/20">0x08</span>{"  "}<span className="text-violet-400"><GlitchWord word="CORTEX" intensity={2} colorBase="violet" /></span>{"  "}<span className="text-white/40">ready</span></p>
                      <p><span className="text-white/15">↳</span>{"     "}<span className="text-white/25">scope: </span><span className="text-white/50">your properties</span></p>
                    </div>
                  </div>
                  <div className="border-t border-white/[0.06] w-full max-w-sm" />
                  <div className="space-y-2">
                    <h3 className="text-lg font-mono font-bold text-white/90 tracking-tight">How can I help you today?</h3>
                    <p className="text-[11px] font-mono text-white/40 leading-relaxed">
                      Predict supply, summarize quality, answer questions about your facilities.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 w-full justify-center">
                    {[
                      { q: "What's my Luster Score?", i: TrendingUp },
                      { q: "Next service?", i: Calendar },
                      { q: "Recent issues", i: Info },
                      { q: "Supply status", i: Package }
                    ].map(({ q, i: Icon }) => (
                      <button
                        key={q}
                        onClick={() => setInput(q)}
                        className="group flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-mono font-bold uppercase tracking-wider text-white/50 bg-white/[0.04] border border-white/[0.08] hover:bg-violet-500/10 hover:border-violet-400/20 hover:text-violet-400 transition-all duration-200"
                      >
                        <Icon className="w-3.5 h-3.5 text-white/40 group-hover:text-violet-400 transition-colors" />
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <ul className="space-y-5">
                  {messages.map((m, i) => (
                    <li
                      key={i}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl text-sm font-mono transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 ${
                          m.role === "user"
                            ? "bg-violet-500/20 border border-violet-400/25 text-white/95 rounded-tr-md"
                            : `${glass.cardBg} border border-white/[0.08] text-white/80 rounded-tl-md backdrop-blur-sm`
                        }`}
                      >
                        {m.role === "assistant" && !m.isPlaceholder ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-strong:font-semibold text-inherit [&_*]:font-mono [&_*]:text-inherit">
                            <ReactMarkdown>{m.text}</ReactMarkdown>
                          </div>
                        ) : (
                          <span className="text-white/60">{m.text}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div ref={endRef} />
            </div>

            <form
              onSubmit={handleSubmit}
              className={`shrink-0 p-4 md:p-6 border-t border-white/[0.06] ${glass.sectionBg}`}
            >
              <div className="flex gap-2 items-center max-w-3xl mx-auto w-full">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about your facility..."
                  className="flex-1 min-w-0 px-4 py-3 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 font-mono text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400/20 transition-all"
                  aria-label="Message"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || assistantLoading}
                  className="shrink-0 w-11 h-11 rounded-xl bg-violet-500/20 border border-violet-400/25 text-violet-400 flex items-center justify-center transition-all hover:bg-violet-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50 disabled:opacity-50 disabled:pointer-events-none active:scale-95"
                  aria-label="Send"
                >
                  {assistantLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setMessages([])}
                  className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-white/40 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:text-white/60 transition-all active:scale-95"
                  aria-label="New chat"
                  title="New chat"
                >
                  <MessageSquarePlus className="w-4 h-4" />
                </button>
              </div>
              {onOpenContact && (
                <div className="mt-3 pt-3 border-t border-white/[0.06] flex justify-center">
                  <button
                    type="button"
                    onClick={onOpenContact}
                    className="flex items-center gap-1.5 text-[10px] font-mono text-white/30 hover:text-white/50 transition-colors uppercase tracking-wider"
                  >
                    <Headphones className="w-3 h-3 shrink-0" />
                    Reach out to speak to a real human
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
